/**
 * RuleRow — one validation rule editor with config inputs, severity selector,
 * error-message override, and an inline preview that runs the engine's
 * `checkValidationRule` against a sample value.
 *
 * Used inside the field-editor-panel's "Validation" tab.
 */
import { useState } from 'react'
import {
  AlertTriangle, ShieldCheck, ChevronDown, ChevronUp, Trash2,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { availableRulesFor, checkValidationRule, ruleSpec } from '@/lib/form-engine'
import type {
  FieldDefinition, FieldType, ValidationRule,
} from '../api/form-builder-api'

interface RuleRowProps {
  rule: ValidationRule
  fieldType: FieldType
  allFields: FieldDefinition[]
  onChange: (r: ValidationRule) => void
  onDelete: () => void
}

export function RuleRow({ rule, fieldType, allFields, onChange, onDelete }: RuleRowProps) {
  const [expanded, setExpanded] = useState(true)
  const [previewValue, setPreviewValue] = useState('')
  const cfg = rule.rule_config ?? {}
  const spec = ruleSpec(fieldType, rule.rule_type)
  const available = availableRulesFor(fieldType)

  const previewInputType =
    fieldType === 'number' ? 'number'
    : fieldType === 'date' ? 'date'
    : fieldType === 'datetime' ? 'datetime-local'
    : fieldType === 'time' ? 'time'
    : 'text'

  const previewParsed =
    previewInputType === 'number' && previewValue !== '' ? Number(previewValue) : previewValue
  const previewResult: 'pass' | 'fail' | null = previewValue === '' ? null
    : checkValidationRule(rule, previewParsed, {}) ? 'fail' : 'pass'

  const patchCfg = (patch: Record<string, unknown>) =>
    onChange({ ...rule, rule_config: { ...cfg, ...patch } })

  const numericKeys = allFields.filter(
    (f) => f.field_type === 'number' && f.field_key !== rule.rule_config?.compare_field_key,
  )
  const dateKeys = allFields.filter(
    (f) => f.field_type === 'date' || f.field_type === 'datetime',
  )

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-muted/30">
        <Checkbox
          checked={rule.is_enabled}
          onCheckedChange={(v) => onChange({ ...rule, is_enabled: Boolean(v) })}
          className="w-3.5 h-3.5 shrink-0"
        />
        <span className="flex-1 text-xs truncate">
          {spec?.label ?? rule.rule_type}
        </span>
        <Badge
          variant={rule.severity === 'soft_warning' ? 'secondary' : 'destructive'}
          className="text-[9px] px-1 h-4 shrink-0 gap-0.5"
        >
          {rule.severity === 'soft_warning'
            ? <><AlertTriangle className="w-2.5 h-2.5" />هشدار</>
            : <><ShieldCheck className="w-2.5 h-2.5" />بلوک‌کننده</>}
        </Badge>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-muted-foreground hover:text-foreground"
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <button onClick={onDelete} className="text-muted-foreground hover:text-destructive">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="px-2.5 py-2 space-y-2 border-t border-border">
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">نوع قانون</Label>
            <Select
              value={rule.rule_type}
              onValueChange={(v) => {
                const next = ruleSpec(fieldType, v)
                onChange({
                  ...rule,
                  rule_type: v,
                  severity: next?.defaultSeverity ?? rule.severity,
                  rule_config: {},
                })
              }}
            >
              <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {available.map((r) => (
                  <SelectItem key={r.type} value={r.type}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(rule.rule_type === 'numeric_range' || rule.rule_type === 'physiological_range') && (
            <div className="grid grid-cols-2 gap-2">
              <ConfigNumber label="حداقل" value={cfg.min} onChange={(v) => patchCfg({ min: v })} />
              <ConfigNumber label="حداکثر" value={cfg.max} onChange={(v) => patchCfg({ max: v })} />
            </div>
          )}

          {rule.rule_type === 'min_length' && (
            <ConfigNumber label="حداقل طول" min={0} value={cfg.min} onChange={(v) => patchCfg({ min: v })} />
          )}
          {rule.rule_type === 'max_length' && (
            <ConfigNumber label="حداکثر طول" min={0} value={cfg.max} onChange={(v) => patchCfg({ max: v })} />
          )}

          {rule.rule_type === 'max_decimal_places' && (
            <ConfigNumber label="حداکثر اعشار" min={0} value={cfg.max_decimals} onChange={(v) => patchCfg({ max_decimals: v })} />
          )}

          {rule.rule_type === 'regex_match' && (
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">الگو (regex)</Label>
              <Input
                value={(cfg.pattern as string | undefined) ?? ''}
                onChange={(e) => patchCfg({ pattern: e.target.value })}
                className="h-7 text-xs font-mono" dir="ltr"
                placeholder="^[0-9]{10}$"
              />
            </div>
          )}

          {(rule.rule_type === 'value_greater_than_field' ||
            rule.rule_type === 'value_less_than_field') && (
            <ConfigFieldSelect
              label="فیلد مرجع"
              value={cfg.compare_field_key as string | undefined}
              candidates={numericKeys}
              onChange={(v) => patchCfg({ compare_field_key: v })}
            />
          )}

          {(rule.rule_type === 'date_after_field' ||
            rule.rule_type === 'date_before_field') && (
            <ConfigFieldSelect
              label="فیلد مرجع"
              value={cfg.compare_field_key as string | undefined}
              candidates={dateKeys}
              onChange={(v) => patchCfg({ compare_field_key: v })}
            />
          )}

          {(rule.rule_type === 'date_after_relative' || rule.rule_type === 'date_before_relative') && (
            <div className="grid grid-cols-2 gap-2">
              <ConfigNumber label="مقدار" value={cfg.offset_value} onChange={(v) => patchCfg({ offset_value: v })} />
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">واحد</Label>
                <Select
                  value={(cfg.offset_unit as string | undefined) ?? 'years'}
                  onValueChange={(v) => patchCfg({ offset_unit: v })}
                >
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">روز</SelectItem>
                    <SelectItem value="months">ماه</SelectItem>
                    <SelectItem value="years">سال</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {(rule.rule_type === 'date_after_absolute' || rule.rule_type === 'date_before_absolute') && (
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">تاریخ</Label>
              <Input
                type="date"
                value={(cfg.date as string | undefined) ?? ''}
                onChange={(e) => patchCfg({ date: e.target.value || undefined })}
                className="h-7 text-xs" dir="ltr"
              />
            </div>
          )}

          {(rule.rule_type === 'time_after' || rule.rule_type === 'time_before') && (
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">ساعت</Label>
              <Input
                type="time"
                value={(cfg.time as string | undefined) ?? ''}
                onChange={(e) => patchCfg({ time: e.target.value || undefined })}
                className="h-7 text-xs" dir="ltr"
              />
            </div>
          )}

          {(rule.rule_type === 'min_selected' || rule.rule_type === 'min_entries') && (
            <ConfigNumber label="حداقل" min={0} value={cfg.min} onChange={(v) => patchCfg({ min: v })} />
          )}
          {(rule.rule_type === 'max_selected' || rule.rule_type === 'max_entries') && (
            <ConfigNumber label="حداکثر" min={0} value={cfg.max} onChange={(v) => patchCfg({ max: v })} />
          )}

          {rule.rule_type === 'file_extension' && (
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">پسوندهای مجاز (با کاما جدا کنید)</Label>
              <Input
                value={Array.isArray(cfg.extensions) ? (cfg.extensions as string[]).join(', ') : ''}
                onChange={(e) =>
                  patchCfg({
                    extensions: e.target.value
                      .split(',')
                      .map((x) => x.trim().replace(/^\./, '').toLowerCase())
                      .filter(Boolean),
                  })
                }
                className="h-7 text-xs font-mono" dir="ltr"
                placeholder="pdf, jpg, png"
              />
            </div>
          )}

          {rule.rule_type === 'max_file_size' && (
            <ConfigNumber label="حداکثر حجم (MB)" min={0} value={cfg.max_mb} onChange={(v) => patchCfg({ max_mb: v })} />
          )}

          {(rule.rule_type === 'max_image_width' || rule.rule_type === 'max_image_height') && (
            <ConfigNumber label="حداکثر پیکسل" min={1} value={cfg.max_px} onChange={(v) => patchCfg({ max_px: v })} />
          )}

          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">شدت</Label>
            <Select
              value={rule.severity}
              onValueChange={(v) => onChange({ ...rule, severity: v as 'blocking' | 'soft_warning' })}
            >
              <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="blocking">خطای بلوک‌کننده — ارسال متوقف می‌شود</SelectItem>
                <SelectItem value="soft_warning">هشدار — کاربر باید توجیه وارد کند</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">پیام خطا (اختیاری)</Label>
            <Input
              value={rule.error_message?.fa ?? ''}
              onChange={(e) =>
                onChange({ ...rule, error_message: e.target.value ? { fa: e.target.value } : undefined })
              }
              className="h-7 text-xs"
              placeholder="پیام سفارشی..."
            />
          </div>

          {previewInputType !== 'text' || ['min_length', 'max_length', 'regex_match'].includes(rule.rule_type) ? (
            <div className="space-y-1 pt-1 border-t border-dashed border-border">
              <Label className="text-[10px] text-muted-foreground">پیش‌نمایش (مقدار نمونه وارد کنید)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type={previewInputType}
                  value={previewValue}
                  onChange={(e) => setPreviewValue(e.target.value)}
                  className="h-7 text-xs flex-1" dir="ltr"
                  placeholder="مقدار نمونه..."
                />
                {previewResult === 'pass' && (
                  <span className="text-[10px] text-green-500 shrink-0">✓ معتبر</span>
                )}
                {previewResult === 'fail' && (
                  <span className="text-[10px] text-destructive shrink-0">✗ خطا</span>
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

function ConfigNumber({ label, min, value, onChange }: {
  label: string; min?: number; value: unknown; onChange: (n: number | undefined) => void
}) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] text-muted-foreground">{label}</Label>
      <Input
        type="number"
        value={(value as number | undefined) ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        className="h-7 text-xs" dir="ltr" min={min}
      />
    </div>
  )
}

function ConfigFieldSelect({ label, value, candidates, onChange }: {
  label: string; value: string | undefined; candidates: FieldDefinition[]; onChange: (k: string) => void
}) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] text-muted-foreground">{label}</Label>
      <Select value={value ?? ''} onValueChange={onChange}>
        <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="انتخاب فیلد..." /></SelectTrigger>
        <SelectContent>
          {candidates.length === 0 && (
            <SelectItem value="__none__" disabled>فیلد سازگاری وجود ندارد</SelectItem>
          )}
          {candidates.map((f) => (
            <SelectItem key={f.field_key} value={f.field_key}>
              {f.label.fa || f.field_key}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
